<template>
  <v-container fluid class="px-0" v-if="!showRouterView">
    <v-row>
      <v-col cols="12">
        <gg-card>
          <gg-card-header>
            List Domains
          </gg-card-header>
          <gg-card-text>
            <v-data-table :items="items"
                          :headers="headers"
                          :loading="pendingDomains"
            >
              <template v-slot:item.status="{ item }">
                <v-progress-circular v-if="pendingDetails" indeterminate color="primary" width="2" size="16" />
                <div v-else v-for="(item, key) in item.status" :key="key">
                  <div v-if="item.type === 'DKIM'">
                    <b>{{ item.type }}</b>: {{ dkimStatus(item.issue) }}
                  </div>
                  <div v-if="item.type === 'SPF'">
                    <b>{{ item.type }}</b>: {{ spfStatus(item.issue) }}
                  </div>
                  <div v-if="item.type === 'Reverse DNS'">
                    <b>{{ item.type }}</b>: {{ ptrsStatus(item.issue) }}
                  </div>
                </div>
              </template>
              <template v-slot:item.actions="{ item }">
                <template v-if="!pendingDomains && !pendingDetails">
                  <template v-if="!item.local_authority">
                    <v-tooltip top max-width="250">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon color="error" dark v-bind="attrs" v-on="on">
                          mdi-alert
                        </v-icon>
                      </template>
                      <span>
                    This system does not control DNS for the "{{ item.domain }}" domain,
                    and the system did not find any authoritative nameservers for this domain.
                    Contact your domain registrar to verify this domain's registration.
                  </span>
                    </v-tooltip>
                  </template>
                </template>
                <gg-btn primary class="ml-5" @click="manageDomain(item.domain)">
                  Manage
                </gg-btn>
              </template>
            </v-data-table>
          </gg-card-text>
        </gg-card>
      </v-col>
    </v-row>
  </v-container>
  <router-view v-else />
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex'
import { isEmpty } from 'Helpers/helpers'

const dkimStatus = {
  'VALID': 'The DKIM record is valid.',
  'MALFORMED': 'A single DKIM record exists, but the record does not match the expected DKIM specifications.',
  'MISMATCH': ' A DKIM record exists, but it does not match the expected public key.',
  'MISSING': 'No DKIM record exists for the domain.',
  'MULTIPLE': 'Multiple DKIM records exist.',
  'NOPUB': 'No key exists on the local server for the domain.',
  'ERROR': 'The record\'s DNS lookup failed. The function returns the reason in the error return.'
}

const spfStatus = {
  'VALID': 'A single SPF TXT record exists in the domain\'s DNS with the correct ip_address value or redirect mechanism.',
  'MISMATCHED': 'An SPF TXT record exists for the domain that does not match the ip_address value.',
  'MISSING': 'No SPF TXT record exists for the domain\'s DNS.',
  'MULTIPLE': 'Multiple SPF TXT records exist in the domain\'s DNS.',
  'ERROR': 'The record\'s DNS lookup failed. The function returns the reason in the error return.'
}

const ptrsStatus = {
  ERROR: 'The domain\'s IP address is invalid. The function returns the reason in the error return.',
  IP_IS_PRIVATE: 'The IP address exists within a range of private IP addresses.',
  VALID: 'The PTR record is valid.',
  MISSING_PTR: 'No PTR records exist for the IP address.',
  PTR_MISMATCH: 'One or more PTR records point to a domain that does not point back to the correct IP address.'
}

export default {
  name: 'AuthenticationView',
  props: {
    // Where Manage leads, as `(domain) => location`. The hosting section's
    // route is the default; the Email section passes its own.
    manageTo: {
      type: Function,
      default: null,
    },
  },
  data: () => ({
    isEmpty,
    pendingDomains: false,
    pendingDetails: false,
    headers: [
      {
        text: 'Domain',
        value: 'domain',
      },
      {
        text: 'Email Deliverability Status',
        value: 'status',
        sortable: false
      },
      {
        text: 'Actions',
        value: 'actions',
        sortable: false,
        align: 'center'
      },
    ],
  }),
  async mounted () {
    await this.loadDomains()
    await this.loadDetails()
  },
  beforeDestroy () {
    this.clearStore()
  },
  computed: {
    ...mapGetters({
      domains: 'email_deliverability/domains',
      queryResult: 'email_deliverability/queryResult'
    }),
    items () {
      if (this.queryResult.length === 0) {
        return this.domains.map((item, i) => {
          return {
            domain: item,
          }
        })
      } else {
        return this.domains.map((item, i) => {
          return {
            domain: item,
            status: [
              {
                type: 'DKIM',
                issue: this.queryResult[1].data[i].state
              },
              {
                type: 'SPF',
                issue: this.queryResult[2].data[i].state
              },
              {
                type: 'Reverse DNS',
                issue: this.queryResult[3].data[i].state
              }
            ],
            local_authority: !!this.queryResult[0].data[i].local_authority,
            ns: this.queryResult[0].nameservers,
          }
        })
      }
    },
    showRouterView () {
      return this.$route.params.domain !== undefined
    }
  },
  methods: {
    ...mapActions({
      getDomains: 'email_deliverability/getDomains',
      batchQuery: 'email_deliverability/batchQuery',
      reportError: 'reportError',
    }),
    ...mapMutations({
      setDomain: 'email_deliverability/SET_DOMAIN',
      clearStore: 'email_deliverability/CLEAR_STORE'
    }),
    async loadDomains () {
      try {
        this.pendingDomains = true
        await this.getDomains(this.$route.query.user)
      } catch (e) {
        console.log(e)
        this.reportError(e)
      } finally {
        this.pendingDomains = false
      }
    },
    async loadDetails () {
      try {
        this.pendingDetails = true
        await this.batchQuery({ _username: this.$route.query.user, single: false })
      } catch (e) {
        this.reportError(e)
        console.log(e)
      } finally {
        this.pendingDetails = false
      }
    },
    dkimStatus (status) {
      return dkimStatus[status]
    },
    spfStatus (status) {
      return spfStatus[status]
    },
    ptrsStatus (status) {
      return ptrsStatus[status]
    },
    manageDomain (domain) {
      this.setDomain(domain)

      if (this.manageTo) {
        this.$router.push(this.manageTo(domain))
        return
      }

      this.$router.push({
        name: 'hosting_email_authentication_manage',
        params: {
          domain: domain
        },
        query: {
          user: this.$route.query.user
        }
      })
    }
  }
}
</script>

<style scoped>

</style>
